import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Play, RefreshCw, CheckCircle, AlertCircle, Clock, Loader } from 'lucide-react';
import { confirmOrdersManual, fetchOrdersByWeek } from '../../store/slices/ordersSlice';
import { selectSeason } from '../../store/slices/seasonSlice';
import { supabase } from '../../config/supabase';
import './ConfigPedidosConfirmados.css';

const ConfigPedidosConfirmados = () => {
  const dispatch = useDispatch();
  const selectedSeason = useSelector(selectSeason);
  const [executing, setExecuting] = useState(false);
  const [lastExecution, setLastExecution] = useState(null);
  const [syncEvents, setSyncEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);

  useEffect(() => {
    loadSyncEvents();
  }, []);

  const loadSyncEvents = async () => {
    setLoadingEvents(true);
    try {
      const { data, error } = await supabase
        .from('sync_events')
        .select('*')
        .in('type', ['order_confirmation_auto', 'order_confirmation_manual', 'order_confirmation_revert'])
        .order('start_time', { ascending: false })
        .limit(20);

      if (error) throw error;
      setSyncEvents(data || []);
    } catch (error) {
      console.error('Error loading sync events:', error);
    } finally {
      setLoadingEvents(false);
    }
  };

  const handleExecuteConfirmation = async () => {
    if (!window.confirm('¿Está seguro que desea ejecutar la confirmación manual de pedidos?\n\nEsto confirmará todos los pedidos cuya semana ya haya finalizado.')) {
      return;
    }

    setExecuting(true);
    try {
      const result = await dispatch(confirmOrdersManual()).unwrap();
      setLastExecution({
        success: true,
        ordersConfirmed: result.ordersConfirmed,
        orderDetails: result.orderDetails,
        message: result.message,
        timestamp: new Date().toISOString()
      });
      
      // Reload sync events
      await loadSyncEvents();
      
      // Reload orders to reflect changes in the UI
      if (selectedSeason) {
        await dispatch(fetchOrdersByWeek({
          year: selectedSeason.year,
          week: selectedSeason.week,
          filters: {}
        }));
      }
    } catch (error) {
      setLastExecution({
        success: false,
        error: error,
        timestamp: new Date().toISOString()
      });
    } finally {
      setExecuting(false);
    }
  };

  const getEventTypeLabel = (eventType) => {
    switch (eventType) {
      case 'order_confirmation_auto':
        return 'Confirmación Automática';
      case 'order_confirmation_manual':
        return 'Confirmación Manual';
      case 'order_confirmation_revert':
        return 'Reversión de Confirmación';
      default:
        return eventType;
    }
  };

  const getEventIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle size={16} className="text-success" />;
      case 'error':
        return <AlertCircle size={16} className="text-error" />;
      default:
        return <Clock size={16} className="text-gray-500" />;
    }
  };

  return (
    <div className="config-pedidos-confirmados">
      <div className="page-header">
        <div>
          <h1>Confirmación de Pedidos</h1>
          <p className="text-gray-600">
            Gestión de confirmación automática y manual de pedidos semanales
          </p>
        </div>
      </div>

      {/* Manual Execution Card */}
      <div className="card execution-card">
        <div className="card-header">
          <h2>Ejecución Manual</h2>
          <p className="text-gray-600">
            Confirma manualmente todos los pedidos cuya semana ya haya finalizado (después del viernes)
          </p>
        </div>

        <div className="execution-actions">
          <button
            className="btn btn-primary"
            onClick={handleExecuteConfirmation}
            disabled={executing}
          >
            {executing ? (
              <>
                <Loader className="animate-spin" size={18} />
                Ejecutando...
              </>
            ) : (
              <>
                <Play size={18} />
                Ejecutar Confirmación
              </>
            )}
          </button>

          <button
            className="btn btn-outline"
            onClick={loadSyncEvents}
            disabled={loadingEvents}
          >
            <RefreshCw size={18} className={loadingEvents ? 'animate-spin' : ''} />
            Actualizar Historial
          </button>
        </div>

        {/* Last Execution Result */}
        {lastExecution && (
          <div className={`execution-result ${lastExecution.success ? 'success' : 'error'}`}>
            <div className="result-icon">
              {lastExecution.success ? (
                <CheckCircle size={24} />
              ) : (
                <AlertCircle size={24} />
              )}
            </div>
            <div className="result-content">
              <h3>{lastExecution.success ? 'Ejecución Exitosa' : 'Error en Ejecución'}</h3>
              {lastExecution.success ? (
                <>
                  <p>
                    <strong>{lastExecution.ordersConfirmed}</strong> pedido(s) confirmado(s)
                  </p>
                  {lastExecution.message && <p className="text-sm">{lastExecution.message}</p>}
                  {lastExecution.orderDetails && lastExecution.orderDetails.length > 0 && (
                    <div className="order-details">
                      <p className="text-sm font-medium">Pedidos confirmados:</p>
                      <ul>
                        {lastExecution.orderDetails.map((order) => (
                          <li key={order.id}>
                            Pedido #{order.id} - Temporada {order.temporada}, Semana {order.semana}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-error">{lastExecution.error}</p>
              )}
              <p className="text-xs text-gray-500 mt-2">
                {new Date(lastExecution.timestamp).toLocaleString('es-CL')}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Sync Events History */}
      <div className="card events-card">
        <div className="card-header">
          <h2>Historial de Ejecuciones</h2>
          <p className="text-gray-600">
            Registro de confirmaciones automáticas, manuales y reversiones
          </p>
        </div>

        {loadingEvents ? (
          <div className="loading-state">
            <Loader className="animate-spin" size={32} />
            <p>Cargando historial...</p>
          </div>
        ) : syncEvents.length > 0 ? (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Estado</th>
                  <th>Tipo</th>
                  <th>Fecha</th>
                  <th>Detalles</th>
                </tr>
              </thead>
              <tbody>
                {syncEvents.map((event) => (
                  <tr key={event.id}>
                    <td>
                      <div className="event-status">
                        {getEventIcon(event.status)}
                        <span className={`badge badge-${event.status === 'success' ? 'success' : 'error'}`}>
                          {event.status === 'success' ? 'Éxito' : 'Error'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className="event-type">
                        {getEventTypeLabel(event.type)}
                      </span>
                    </td>
                    <td>
                      <div className="event-date">
                        <Clock size={14} />
                        {new Date(event.start_time).toLocaleString('es-CL')}
                      </div>
                    </td>
                    <td>
                      <div className="event-details">
                        {event.result?.orders_confirmed !== undefined && (
                          <span className="detail-badge">
                            {event.result.orders_confirmed} pedido(s)
                          </span>
                        )}
                        {event.result?.order_id && (
                          <span className="detail-badge">
                            Pedido #{event.result.order_id}
                          </span>
                        )}
                        {event.result?.user_email && (
                          <span className="detail-badge user">
                            {event.result.user_email}
                          </span>
                        )}
                        {event.result?.error && (
                          <span className="detail-badge error">
                            {event.result.error}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <Clock size={48} className="text-gray-400" />
            <h3>No hay eventos registrados</h3>
            <p>Las ejecuciones de confirmación aparecerán aquí</p>
          </div>
        )}
      </div>

      {/* Information Card */}
      <div className="card info-card">
        <div className="card-header">
          <h2>Información</h2>
        </div>
        <div className="info-content">
          <div className="info-item">
            <h3>Confirmación Automática</h3>
            <p>
              Se ejecuta diariamente a las 00:00 UTC (21:00 hora Chile en verano, 20:00 en invierno).
              Confirma automáticamente todos los pedidos cuya semana ya haya finalizado (después del viernes).
            </p>
          </div>
          <div className="info-item">
            <h3>Confirmación Manual</h3>
            <p>
              Permite ejecutar la confirmación de pedidos de forma inmediata sin esperar la ejecución automática.
              Útil para confirmar pedidos urgentemente o después de realizar cambios.
            </p>
          </div>
          <div className="info-item">
            <h3>Reversión de Confirmación</h3>
            <p>
              Solo usuarios con rol Administrador o Producción pueden revertir pedidos confirmados a estado "Creado".
              Esto permite editar pedidos que ya fueron confirmados en caso de ser necesario.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigPedidosConfirmados;
